import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import ClausPortrait from '../assets/Claus.JPG';
import RasmusPortrait from '../assets/Rasmus.JPG';
import CarlosPortrait from '../assets/Carlos.JPG';

const principles = [
  {
    title: 'Målrettethed',
    icon: '🔐',
    description: 'Vi arbejder altid med klare mål og sikrer, at indsatsen skaber tydelig værdi.',
  },
  {
    title: 'Ordentlighed',
    icon: '🚀',
    description: 'Vi optræder med respekt, transparens og leverer på det, vi lover.',
  },
  {
    title: 'Udvikling',
    icon: '🫱🏻‍🫲🏽',
    description: 'Vi bliver ved med at lære, udfordre og forbedre, sammen med vores kunder.',
  },
];

const toTelHref = (phone) => phone.replace(/[^+\d]/g, '');

const team = [
  {
    name: 'Claus Møldrup',
    title: 'Founder, AICE',
    email: 'cm@aice.dk',
    phone: '+45 31 45 03 88',
    photo: ClausPortrait,
    blurb:
      '20+ års erfaring som managementkonsulent, investor og leder. Gør komplekse teknologier praksisnære og værdiskabende for beslutningstagere.',
  },
  {
    name: 'Rasmus Kaltoft',
    title: 'Co-founder, AICE',
    email: 'rka@aice.dk',
    phone: '+45 61 44 77 67',
    photo: RasmusPortrait,
    blurb:
      '20+ års erfaring med at udvikle forretning gennem teknologi og partnerskaber. Omsætter AI ambitioner til praksis for både ledelse og fagfolk.',
  },
  {
    name: 'Carlos Hebbinghaus',
    title: 'AI Technical Lead, AICE',
    email: 'ch@aice.dk',
    phone: '+45 53 59 15 30',
    photo: CarlosPortrait,
    blurb:
      'Bygger bro mellem teknik og forretning med automatiseringer, Copilot/GPT-løsninger og AI-assistenter, der skaber værdi i hverdagen.',
  },
];

const About = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    if (window.location.hash === '#meet-team') {
      const el = document.getElementById('meet-team');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
  <div style={pageStyle}>
    <SeoHead
      title="Om AICE | AI-partner for vidensvirksomheder"
      description="Mød teamet bag AICE og vores mission om at gøre AI konkret, tryg og værdiskabende. Læs om værdierne, erfaringen og kontaktdetaljer."
      canonicalPath="/about"
    />
    <section style={heroSection}>
      <p style={eyebrow}>Om AICE</p>
      <h1 style={heroTitle}>AICE gør AI forståeligt, brugbart og værdifuldt</h1>
      <p style={heroText}>
        AICE er skabt med én mission: at gøre AI konkret, trygt og strategisk relevant for vidensvirksomheder. Vi
        arbejder i krydsfeltet mellem forretning, teknologi og mennesker – og hjælper jer med at navigere trygt i en
        kompleks tid.
      </p>
    </section>

    <section style={featureSection}>
      <article style={featureCard}>
        <h3 style={featureTitle}>Vi møder jer i øjenhøjde</h3>
        <p style={featureText}>
          Hos AICE kombinerer vi teknologisk forståelse med forretningssans. Vi siger det, som det er – og vi ved, at AI
          ikke er en løsning i sig selv, men et redskab der først giver værdi, når det hænger sammen med strategi,
          kultur og kompetencer.
        </p>
      </article>
      <article style={featureCard}>
        <h3 style={featureTitle}>Vores rolle: Troldmanden</h3>
        <p style={featureText}>
          Vi elsker at tage noget komplekst – og forvandle det til noget brugbart og kraftfuldt. Ikke med røg og spejle,
          men med struktur, indsigt og menneskelig forståelse. Det handler ikke om AI som teknologi – men som mulighed.
        </p>
      </article>
    </section>

    <section style={valuesSection}>
      <h2 style={sectionHeading}>Vores værdier</h2>
      <div style={valuesGrid}>
        {principles.map((item) => (
          <article key={item.title} style={valueCard}>
            <span style={valueIcon}>{item.icon}</span>
            <div>
              <h3 style={valueTitle}>{item.title}</h3>
              <p style={valueText}>{item.description}</p>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section id="meet-team" style={teamSection}>
      <h2 style={sectionHeading}>Mød menneskene bag AICE</h2>
      <p style={sectionSubtext}>
        Vi er et lille hold med dyb erfaring i strategi, teknologi og facilitering. Vi brænder for at gøre AI til noget,
        du faktisk har lyst til at arbejde med.
      </p>
      <div style={teamGrid}>
        {team.map((person) => {
          const isRasmus = person.name === 'Rasmus Kaltoft';
          const isHovered = hoveredCard === person.name;
          return (
            <article
              key={person.title}
              style={{
                ...teamCard,
                ...(isRasmus ? {
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                  ...(isHovered ? {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 22px 44px rgba(192, 54, 38, 0.15)',
                    borderColor: '#E8907F',
                  } : {}),
                } : {}),
              }}
              onClick={isRasmus ? () => navigate('/rasmus-kaltoft') : undefined}
              onMouseEnter={isRasmus ? () => setHoveredCard(person.name) : undefined}
              onMouseLeave={isRasmus ? () => setHoveredCard(null) : undefined}
              role={isRasmus ? 'button' : undefined}
              tabIndex={isRasmus ? 0 : undefined}
              onKeyDown={isRasmus ? (e) => e.key === 'Enter' && navigate('/rasmus-kaltoft') : undefined}
            >
              <img src={person.photo} alt={person.name} style={teamPhoto} />
              <p style={teamName}>{person.name}</p>
              <p style={teamRole}>{person.title}</p>
              {isRasmus && (
                <p style={coachingTag}>AI Coaching →</p>
              )}
              <div style={teamContactBlock}>
                <a href={`mailto:${person.email}`} style={contactLink} onClick={(e) => e.stopPropagation()}>
                  {person.email}
                </a>
                <a href={`tel:${toTelHref(person.phone)}`} style={contactLink} onClick={(e) => e.stopPropagation()}>
                  {person.phone}
                </a>
              </div>
              <p style={teamBlurb}>{person.blurb}</p>
            </article>
          );
        })}
      </div>
      <a href="mailto:cm@aice.dk" style={linkReset}>
        <button
          style={buttonStyle}
          onMouseOver={(e) => {
            e.target.style.background = 'none';
            e.target.style.color = '#C03626';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #C03626, #FF6F61)';
            e.target.style.color = 'white';
          }}
        >
          Kontakt os
        </button>
      </a>
    </section>
  </div>
  );
};

export default About;

const buttonStyle = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: 'white',
  padding: '0.75rem 1.5rem',
  borderRadius: '10px',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  fontSize: '1.02rem',
  marginTop: '1.25rem',
};

const pageStyle = {
  background: 'linear-gradient(180deg, #FFF2EE 0%, #FFFFFF 60%)',
  minHeight: '100vh',
  padding: '4rem clamp(1rem, 5vw, 5rem)',
  color: '#2B1610',
};

const heroSection = {
  maxWidth: '960px',
  margin: '0 auto 3rem',
  textAlign: 'center',
};

const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.25em',
  color: '#C03626',
  fontSize: '0.85rem',
  marginBottom: '0.6rem',
};

const heroTitle = {
  fontSize: '3rem',
  margin: 0,
  color: '#2A0D05',
};

const heroText = {
  marginTop: '1rem',
  fontSize: '1.2rem',
  color: '#5C3A32',
  lineHeight: 1.65,
};

const featureSection = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1.5rem',
  marginBottom: '3rem',
};

const featureCard = {
  background: '#fff',
  borderRadius: '18px',
  padding: '1.75rem',
  border: '1px solid #F5DAD3',
  boxShadow: '0 16px 32px rgba(192, 54, 38, 0.08)',
};

const featureTitle = {
  margin: '0 0 0.75rem',
  fontSize: '1.4rem',
  color: '#2B1610',
};

const featureText = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
};

const sectionHeading = {
  fontSize: '2.2rem',
  margin: '0 0 1rem',
  color: '#2A0D05',
  textAlign: 'center',
};

const valuesSection = {
  marginBottom: '3.5rem',
};

const valuesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '1rem',
};

const valueCard = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  border: '1px solid #F1D5CF',
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'flex-start',
  boxShadow: '0 12px 30px rgba(192, 54, 38, 0.06)',
};

const valueIcon = {
  fontSize: '1.5rem',
};

const valueTitle = {
  margin: '0 0 0.3rem',
  fontSize: '1.1rem',
  color: '#2B1610',
};

const valueText = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.5,
};

const teamSection = {
  marginBottom: '3rem',
  textAlign: 'center',
};

const sectionSubtext = {
  margin: '0 auto 1.5rem',
  maxWidth: '760px',
  color: '#5C3A32',
  lineHeight: 1.6,
};

const teamGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '1rem',
  marginTop: '1rem',
};

const teamCard = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  border: '1px solid #F5DAD3',
  textAlign: 'left',
  boxShadow: '0 12px 30px rgba(192, 54, 38, 0.06)',
};

const teamPhoto = {
  width: '100%',
  borderRadius: '12px',
  objectFit: 'cover',
  height: '300px',
  marginBottom: '1rem',
  border: '1px solid #F5DAD3',
  maxWidth: '400px'
};

const teamName = {
  margin: '0 0 0.25rem',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#2B1610',
};

const teamRole = {
  margin: '0 0 0.35rem',
  fontWeight: 500,
  color: '#5C3A32',
};

const teamContactBlock = {
  margin: '0.25rem 0 0.6rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontWeight: 600,
  wordBreak: 'break-word',
};

const contactLink = {
  color: '#C03626',
  textDecoration: 'none',
};

const teamBlurb = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
};

const linkReset = {
  textDecoration: 'none',
};

const coachingTag = {
  margin: '0 0 0.25rem',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: '#C03626',
};
